import React from 'react';

const PerdaFinanceiraSeguro: React.FC = () => {
  return (
    <section className="relative bg-gradient-to-br from-orange-50/60 via-white to-amber-50/40 py-12 sm:py-16 lg:py-28 overflow-hidden">
      {/* Background pattern overlay - very subtle */}
      <div className="absolute inset-0 opacity-[0.025]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}
        ></div>
      </div>

      {/* Soft accent glows - very light */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-100/60 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-100/50 rounded-full blur-3xl pointer-events-none"></div>

      <div className="container mx-auto px-5 sm:px-6 lg:px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center min-h-[500px] lg:min-h-[620px]">
          
          {/* Coluna Esquerda - Conteúdo */}
          <div className="space-y-6 sm:space-y-8">
            
            {/* Badge Superior */}
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-full px-4 sm:px-5 py-2">
              <i className="ri-money-dollar-circle-line text-orange-600 text-base sm:text-lg"></i>
              <span className="text-orange-700 font-bold text-xs sm:text-sm tracking-[0.15em] uppercase">Custo Oculto</span>
            </div>

            {/* Título */}
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-slate-900 leading-[1.15]">
              Você pode estar{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                pagando mais caro
              </span>{' '}
              por um seguro pior
            </h2>

            {/* Texto introdutório */}
            <p className="text-slate-600 text-base sm:text-lg lg:text-xl leading-relaxed">
              Sem uma análise técnica profissional, você pode estar:
            </p>

            {/* Lista Estratégica */}
            <div className="space-y-3 sm:space-y-4">
              {[
                {
                  icon: 'ri-money-dollar-circle-line',
                  title: 'Pagando por coberturas desnecessárias'
                },
                {
                  icon: 'ri-shield-cross-line',
                  title: 'Deixando de contratar proteções essenciais'
                },
                {
                  icon: 'ri-contract-line',
                  title: 'Aceitando condições desfavoráveis'
                },
                {
                  icon: 'ri-refresh-line',
                  title: 'Renovando sem revisão estratégica'
                }
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 sm:gap-4 bg-white border border-slate-200 rounded-lg sm:rounded-xl p-4 sm:p-5 hover:border-orange-300 hover:bg-orange-50/30 transition-all duration-300 group"
                >
                  <div className="flex-shrink-0 w-9 h-9 sm:w-11 sm:h-11 bg-orange-50 rounded-lg flex items-center justify-center border border-orange-200 group-hover:border-orange-300 transition-colors">
                    <i className={`${item.icon} text-orange-500 text-lg sm:text-xl`}></i>
                  </div>
                  <p className="text-slate-700 font-medium text-sm sm:text-base lg:text-lg leading-relaxed pt-1">
                    {item.title}
                  </p>
                </div>
              ))}
            </div>

            {/* Bloco de Autoridade IBX Brasil */}
            <div className="relative bg-white border border-slate-200 border-l-4 border-l-emerald-500 rounded-xl sm:rounded-2xl p-5 sm:p-6 lg:p-7">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0 w-11 h-11 sm:w-14 sm:h-14 bg-emerald-50 rounded-lg sm:rounded-xl flex items-center justify-center border border-emerald-200">
                  <i className="ri-line-chart-line text-emerald-600 text-xl sm:text-2xl"></i>
                </div>
                <div>
                  <div className="text-emerald-700 font-bold text-[10px] sm:text-xs uppercase tracking-wider mb-1.5 sm:mb-2">
                    Modelo IBX Brasil
                  </div>
                  <p className="text-slate-800 text-base sm:text-lg lg:text-xl leading-relaxed font-semibold">
                    O modelo da IBX Brasil foi criado para eliminar desperdícios e estruturar seguros com máxima eficiência.
                  </p>
                </div>
              </div>
            </div>

            {/* Indicadores Visuais */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 pt-1">
              {[
                { label: 'Mais análise', desc: 'Menos desperdício contratual' },
                { label: 'Mais precisão', desc: 'Coberturas mais eficientes' },
                { label: 'Mais estratégia', desc: 'Melhor relação custo e proteção' }
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white border border-slate-200 rounded-lg p-4 hover:border-emerald-300 hover:bg-emerald-50/20 transition-all duration-300"
                >
                  <div className="text-emerald-700 font-bold text-xs sm:text-sm uppercase tracking-wider mb-1">
                    {item.label}
                  </div>
                  <div className="text-slate-500 text-xs leading-snug">
                    {item.desc}
                  </div>
                </div>
              ))}
            </div>

            {/* Destaque Final */}
            <div className="relative bg-slate-900 rounded-xl sm:rounded-2xl p-5 sm:p-6 shadow-lg">
              <div className="absolute -left-0 top-1/2 -translate-y-1/2 w-1 h-14 sm:h-16 bg-gradient-to-b from-orange-500 to-red-500 rounded-r"></div>
              <div className="flex items-start gap-2.5 sm:gap-3 pl-3">
                <i className="ri-double-quotes-l text-orange-400/30 text-2xl sm:text-3xl flex-shrink-0"></i>
                <p className="text-white text-base sm:text-lg lg:text-xl font-bold leading-relaxed">
                  Seguro mal estruturado custa caro — e protege pouco.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="pt-2 sm:pt-4">
              <a
                href="#analise-gratuita"
                className="group flex sm:inline-flex items-center justify-center gap-2 sm:gap-3 bg-gradient-to-r from-action-orange-500 to-red-500 hover:from-action-orange-600 hover:to-red-600 text-white font-bold px-6 sm:px-8 py-4 sm:py-5 rounded-xl transition-all duration-300 w-full sm:w-auto text-center"
              >
                <i className="ri-lightbulb-line text-xl sm:text-2xl shrink-0"></i>
                <span className="text-sm sm:text-base lg:text-lg leading-tight">Quero reduzir custos com mais inteligência</span>
                <i className="ri-arrow-right-line text-lg sm:text-xl group-hover:translate-x-1 transition-transform shrink-0"></i>
              </a>
              <p className="text-slate-500 text-xs sm:text-sm mt-3 sm:mt-4 italic flex items-center justify-center sm:justify-start gap-2">
                <i className="ri-shield-star-line text-emerald-500"></i>
                Análise sem custo. Economia real desde a primeira revisão.
              </p>
            </div>

          </div>

          {/* Coluna Direita - Imagem */}
          <div className="relative h-full min-h-[400px] sm:min-h-[500px] lg:min-h-[620px] mt-4 lg:mt-0">
            <div className="relative h-full rounded-xl sm:rounded-2xl overflow-hidden">
              {/* Imagem Principal */}
              <img
                src="https://readdy.ai/api/search-image?query=sophisticated%20business%20executive%20analyzing%20financial%20reports%20and%20insurance%20documents%20spreadsheets%20on%20modern%20laptop%20in%20elegant%20corporate%20office%20focused%20expression%20comparing%20costs%20and%20coverage%20professional%20lighting%20premium%20photography%20style%20high%20quality%20realistic%20style%20strategic%20decision%20making%20atmosphere%20with%20calculator%20and%20contract%20papers%20visible&width=800&height=900&seq=perda-financeira-seguro-main&orientation=portrait"
                alt="Executivo analisando custos de seguros e relatórios financeiros"
                className="w-full h-full object-cover"
              />
              
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/10 to-transparent"></div>

              {/* Badge superior direito - Estatística */}
              <div className="absolute top-4 sm:top-6 right-4 sm:right-6 bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-lg sm:rounded-xl px-4 sm:px-5 py-3 sm:py-4 border border-orange-400/50">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                  <i className="ri-money-dollar-circle-fill text-base sm:text-lg"></i>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider opacity-90">Custo</span>
                </div>
                <div className="text-xl sm:text-2xl lg:text-3xl font-bold leading-none">42%</div>
                <div className="text-[10px] sm:text-xs font-medium opacity-90 whitespace-nowrap">pagam mais do que deveriam</div>
              </div>

              {/* Card Flutuante Inferior */}
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 border border-slate-200">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-600 to-red-600 rounded-lg flex items-center justify-center">
                    <i className="ri-error-warning-line text-white text-xl sm:text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-slate-900 font-bold text-base sm:text-lg mb-0.5 sm:mb-1">Custo oculto</h4>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                      Você pode estar pagando mais e protegendo menos
                    </p>
                  </div>
                </div>
              </div>

              {/* Card adicional - economia potencial */}
              <div className="absolute top-1/2 -translate-y-1/2 left-4 sm:left-6 bg-white rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-200 max-w-[140px] sm:max-w-none">
                <div className="flex items-center gap-2">
                  <i className="ri-arrow-down-circle-fill text-emerald-600 text-xl sm:text-2xl shrink-0"></i>
                  <div>
                    <div className="text-emerald-700 font-bold text-base sm:text-lg leading-none">até 30%</div>
                    <div className="text-slate-600 text-[10px] sm:text-xs font-medium whitespace-nowrap">economia possível</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default PerdaFinanceiraSeguro;